import React, { useContext } from 'react'
import { useHistory } from 'react-router'
import { BigNumber } from 'ethers'
import { AssetType, GraphPosition } from '@arcxgame/contracts/dist/arc-types/core'
import { useWeb3React } from '@web3-react/core'
import { BASE } from 'src/constants/crypto'
import { eqIgnoreCase } from '@arcxmoney/arcx-core'
import { formatAddress } from 'src/utils/format/formatAddress'

import { calculateCollateralRatio } from '../../helpers/calculateCollateralRatio'
import { ArcContext } from '../../contexts/ArcContext'
import { getCollateralStatus, getStatusString } from '../../helpers/getCollateralStatus'
import { getReadableNumber } from '../../helpers/getReadableNumber'
import { Spinner } from '../Spinner'
import './Positions.scss'
import { getConfigForNetwork } from 'src/config'
import { usePositions } from 'src/hooks/usePositions'

interface IProps {
  onlyShowMyPositions?: boolean
}

export function Positions(props: IProps) {
  const context = useWeb3React()
  const { account } = context
  const history = useHistory()

  const { positions: positionsData, loading } = usePositions(
    props.onlyShowMyPositions ? account : undefined,
    2000,
  )

  const { getPrice, getSynth, getSynthByContainingName, network, isPlaynetActivated } =
    useContext(ArcContext)
  const linkUsdSynthId = getSynthByContainingName('LinkUsd')[0]?.id
  const positions = positionsData?.find(
    ({ synth, owner }: GraphPosition) =>
      eqIgnoreCase(account, owner) && eqIgnoreCase(synth, linkUsdSynthId),
  )
    ? positionsData
    : positionsData?.filter(({ synth }: GraphPosition) => !eqIgnoreCase(synth, linkUsdSynthId))

  const volatileCollaterals = network
    ? getConfigForNetwork(network, isPlaynetActivated).volatileCollaterals
    : []

  function returnTableRow(position: GraphPosition) {
    const truncatedOwner = formatAddress(position.owner)

    if (!position) {
      return
    }
    const synth = getSynth(position.synth)
    if (!synth) {
      return
    }

    const borrowedValue = BigNumber.from(position.borrowedAmountValue)
      .mul(synth.borrowIndex)
      .div(BASE)

    const collateralRatio = calculateCollateralRatio(
      AssetType.Collateral,
      BigNumber.from(position.collateralAmountValue || 0),
      BigNumber.from(position.borrowedAmountValue || 0),
      getPrice(synth.oracle),
    )

    const status = getCollateralStatus(volatileCollaterals, synth, collateralRatio)

    const idString = position.id.split('-')[1]

    return (
      <tr onClick={redirectToVault} key={position.id}>
        <td>{BigNumber.from(idString).toString()}</td>
        <td>
          {getReadableNumber(borrowedValue)}
          &nbsp;{synth.synthetic}
        </td>
        <td>
          {getReadableNumber(position.collateralAmountValue)}
          &nbsp;{synth.collateral}
        </td>
        <td>{collateralRatio.toString()}%</td>
        <td style={{ color: status }}>{getStatusString(status)}</td>
        <td>{truncatedOwner}</td>
      </tr>
    )

    function redirectToVault() {
      return history.push(`/vaults/${position.id.toString()}`)
    }
  }

  if (loading) {
    return <Spinner />
  }
  return (
    <table className="positions-table">
      <thead>
        <tr>
          <th>Vault</th>
          <th>Minted</th>
          <th>Deposited</th>
          <th>C-Ratio</th>
          <th>Status</th>
          <th>Owner</th>
        </tr>
      </thead>
      <tbody>{positions?.map((data) => returnTableRow(data))}</tbody>
    </table>
  )
}
